// ThumbImage — a thumbnail <img> with a built-in skeleton placeholder
// (Jun 19). Tiles stream their actual bytes from GCS, so even with the
// metadata bundled instantly the image area flashes blank until it decodes.
// This renders a pulsing skeleton that sits ON TOP of the image and fades
// out on load (so the image never flashes — it's revealed, not painted in).
// On error the skeleton is replaced by a neutral icon panel.
//
// ASSUMES a positioned ancestor that defines the box — i.e. the existing
// `relative aspect-* overflow-hidden` tile wrapper. The image + skeleton
// both fill it (absolute inset-0). Only use this where the parent provides
// a fixed aspect; for native-aspect masonry tiles a fill skeleton can't
// reserve height without causing load-time reflow.
//
// The skeleton is the overlay (not the image) so the image keeps ALL caller
// classes untouched — including hover transforms — with no transition
// conflicts.

import { useCallback, useEffect, useRef, useState } from 'react';

interface ThumbImageProps {
  src: string;
  alt: string;
  /** Extra classes for the <img> (e.g. hover scale, transitions). */
  className?: string;
  /** object-fit. Default 'cover'. */
  fit?: 'cover' | 'contain';
  /** Forwarded to <img loading>. Omit (eager) inside opacity-animated
   *  sections — Chrome's lazy IntersectionObserver doesn't fire there
   *  (Gotcha #277). Pass 'lazy' only on plain, non-animated grids. */
  loading?: 'lazy' | 'eager';
  draggable?: boolean;
}

export default function ThumbImage({
  src,
  alt,
  className = '',
  fit = 'cover',
  loading,
  draggable,
}: ThumbImageProps) {
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const imgRef = useRef<HTMLImageElement>(null);
  // Literal class strings so Tailwind's JIT sees them (Gotcha #19 — never
  // build `object-${fit}` dynamically).
  const fitClass = fit === 'contain' ? 'object-contain' : 'object-cover';

  // Reveal only once the bytes are DECODED. img.decode() forces the decode even
  // when the tile sits inside an opacity:0 framer-motion section — Chrome
  // otherwise defers the decode/paint until a repaint, so the skeleton fades out
  // (onLoad fired) but the image stays blank until you hover (Jul 14 bug). On
  // failure fall back to naturalWidth. (decode() unsupported → just reveal.)
  const reveal = useCallback(() => {
    const img = imgRef.current;
    if (!img) { setState('loaded'); return; }
    if (typeof img.decode === 'function') {
      img.decode()
        .then(() => setState('loaded'))
        .catch(() => setState(img.naturalWidth > 0 ? 'loaded' : 'error'));
    } else {
      setState('loaded');
    }
  }, []);

  // Cached images can finish loading BEFORE React attaches onLoad → the event
  // never fires and the skeleton sticks. Check `complete` on mount + src change;
  // reset to skeleton for a not-yet-loaded new src.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    if (img.complete) {
      if (img.naturalWidth === 0) setState('error');
      else reveal();
    } else {
      setState('loading');
    }
  }, [src, reveal]);

  return (
    <>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading={loading}
        draggable={draggable}
        decoding="async"
        onLoad={reveal}
        onError={() => setState('error')}
        className={`absolute inset-0 w-full h-full ${fitClass} ${className}`}
      />
      <span
        aria-hidden
        className={`absolute inset-0 pointer-events-none transition-opacity duration-300
          ${state === 'loaded' ? 'opacity-0' : 'opacity-100'}
          ${state === 'error'
            ? 'flex items-center justify-center bg-surface'
            : 'thumb-skeleton'}`}
      >
        {state === 'error' && (
          <i className="ri-image-line text-dark-muted/40 text-3xl" />
        )}
      </span>
    </>
  );
}
